import { AnalyticsEvents } from "@next-platform/client-common-core";
import { IPurchaseOptions } from "@src/types";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { LOCKED_FLOW_QUERY_KEY, LOCKED_FLOW_QUERY_VALUE } from "@/constants";
import useQueueLogEvent from "@/hooks/useQueueLogEvent";
import { getPurchaseData, onSubscribeSticky } from "@/utils";

type UseHandlePlanSelectParams = {
  purchaseOptions: IPurchaseOptions | null;
};

// Navigate directly to checkout — the checkout route is under AppLayout isProtected,
// which handles unauthenticated users via redirect-then-return. Dispatching
// setIsOverlayLogin here would silently no-op because Navigation (which renders
// OverlayLogin) is not mounted in the minimal locked-flow layout.
const useHandlePlanSelect = ({ purchaseOptions }: UseHandlePlanSelectParams) => {
  const navigate = useNavigate();
  const { logEvent } = useQueueLogEvent();

  return useCallback(
    (planId: string, planPrice: number, planCurrency: string) => {
      const allPlans = [
        ...(purchaseOptions?.monthly_plans ?? []),
        ...(purchaseOptions?.quarterly_plans ?? []),
        ...(purchaseOptions?.annual_plans ?? []),
      ];
      const selectedPlan = allPlans.find((plan) => plan.plan_id === planId);
      const { tier } = getPurchaseData(selectedPlan);

      logEvent(AnalyticsEvents.SUBSCRIPTION_PLAN_SELECT, { plan_id: planId, plan_name: tier });
      logEvent(AnalyticsEvents.SUBSCRIPTION_MONEY_VALUE, { currency: planCurrency, value: planPrice });

      const route = `${onSubscribeSticky(planId)}&${LOCKED_FLOW_QUERY_KEY}=${LOCKED_FLOW_QUERY_VALUE}`;
      navigate(route, { state: { purchaseOptions } });
    },
    [purchaseOptions, navigate, logEvent],
  );
};

export default useHandlePlanSelect;
